<script setup lang="ts">
import Dialog from 'primevue/dialog'
import InputText from 'primevue/inputtext';
import Button from 'primevue/button';
import FieldSet from 'primevue/fieldset';
import { ColorPicker, InputGroup, InputGroupAddon } from 'primevue';
import { computed, ref } from 'vue';
import Message from 'primevue/message';
import ProblemDetailsView from 'shared/problems/components/ProblemDetailsView.vue';
import type { ProblemDetails } from "shared/problems/domain/problemDetails";
import FormulaEditor from '@/common/components/FormulaEditor.vue';
import { useUpdateMapStyle, type UpdateMapStyleRequest } from '../api/updateMapStyle';
import { useCreateMapStyle, type CreateMapStyleRequest } from '../api/createMapStyle';
import { useLanguageStore } from 'shared/translations/stores/languageStore';
import { storeToRefs } from 'pinia';
import Splitter from 'primevue/splitter';
import SplitterPanel from 'primevue/splitterpanel';
import PreviewMapView from './PreviewMapView.vue';
import { createMapStyle, type CreateMapStyleResult } from 'shared/mapStyles/domain/createMapStyle';
import type { CompilationResult } from '@datapolarna/formulaparser';
import { language as shapeLanguage } from 'shared/mapStyles/domain/shapeFormulaLanguage';
import { language as vehicleLanguage } from 'shared/mapStyles/domain/vehicleFormulaLanguage';
import type { MapStyleResponse } from 'shared/mapStyles/api/mapStyleResponse';

defineExpose({
  show
});

const styleId = ref<string>();

const createMapStyleApi = useCreateMapStyle();
const updateMapStyle = useUpdateMapStyle();

const languageStore = useLanguageStore();
const { translations } = storeToRefs(languageStore);

const dialogTranslations = translations.value.mapStyles.editDialog;

const visible = ref<boolean>();

const loading = ref(false);
const problems = ref<ProblemDetails>();

const initialValues = ref<ShowDialog>();

const title = ref('');

const name = ref('');

const color = ref<string>('4CAF50');

const nameChanged = computed(() => {
  if (initialValues.value === undefined) return false;
  return initialValues.value.name !== name.value;
});

const nameInvalid = computed(() => {
  if (name.value === '') {
    return translations.value.error.nameIsRequired;
  }

  return undefined;
})

const shapeLabelFormula = ref('');

const shapeLabelFormulaChanged = computed(() => {
  if (initialValues.value === undefined) return false;
  return initialValues.value.shapeLabelFormula !== shapeLabelFormula.value;
});

const shapeColorFormula = ref('');

const shapeColorFormulaChanged = computed(() => {
  if (initialValues.value === undefined) return false;
  return initialValues.value.shapeColorFormula !== shapeColorFormula.value;
});

const shapeOutlineColorFormula = ref('');

const shapeOutlineColorFormulaChanged = computed(() => {
  if (initialValues.value === undefined) return false;
  return initialValues.value.shapeOutlineColorFormula !== shapeOutlineColorFormula.value;
});

const shapeOutlineSizeFormula = ref('');

const shapeOutlineSizeFormulaChanged = computed(() => {
  if (initialValues.value === undefined) return false;
  return initialValues.value.shapeOutlineSizeFormula !== shapeOutlineSizeFormula.value;
});

const vehicleColorFormula = ref('');

const vehicleColorFormulaChanged = computed(() => {
  if (initialValues.value === undefined) return false;
  return initialValues.value.vehicleColorFormula !== vehicleColorFormula.value;
});

const shapeFontSizeFormula = ref('');

const shapeFontSizeFormulaChanged = computed(() => {
  if (initialValues.value === undefined) return false;
  return initialValues.value.shapeFontSizeFormula !== shapeFontSizeFormula.value;
});

const shapeMessageIconFormula = ref('');

const shapeMessageIconFormulaChanged = computed(() => {
  if (initialValues.value === undefined) return false;
  return initialValues.value.shapeMessageIconFormula !== shapeMessageIconFormula.value;
});


const previewMapStyle = computed<CreateMapStyleResult>(() => {
  const previewResponse: MapStyleResponse = {
    id: crypto.randomUUID(),
    name: 'Preview',
    shapeColorFormula: shapeColorFormula.value,
    shapeLabelFormula: shapeLabelFormula.value,
    shapeOutlineColorFormula: shapeOutlineColorFormula.value,
    shapeOutlineSizeFormula: shapeOutlineSizeFormula.value,
    shapeFontSizeFormula: shapeFontSizeFormula.value,
    shapeMessageIconFormula: shapeMessageIconFormula.value,
    vehicleColorFormula: vehicleColorFormula.value,
  };

  return createMapStyle({
    style: previewResponse
  });
});

const shapeLabelCompilationMessage = computed(() => compilationMessage(previewMapStyle.value.compilation.shapeLabel));
const shapeColorCompilationMessage = computed(() => compilationMessage(previewMapStyle.value.compilation.shapeColor));
const shapeOutlineColorCompilationMessage = computed(() => compilationMessage(previewMapStyle.value.compilation.shapeOutlineColor));
const shapeOutlineSizeCompilationMessage = computed(() => compilationMessage(previewMapStyle.value.compilation.shapeOutlineSize));
const shapeFontSizeCompilationMessage = computed(() => compilationMessage(previewMapStyle.value.compilation.shapeFontSize));
const shapeMessageIconCompilationMessage = computed(() => compilationMessage(previewMapStyle.value.compilation.shapeMessageIcon));
const vehicleColorCompilationMessage = computed(() => compilationMessage(previewMapStyle.value.compilation.vehicleColor));

interface ShowDialog {
  id?: string;
  name?: string | null;
  shapeLabelFormula?: string | null;
  shapeColorFormula?: string | null;
  shapeOutlineColorFormula?: string | null;
  shapeOutlineSizeFormula?: string | null;
  vehicleColorFormula?: string | null;
  shapeFontSizeFormula?: string | null;
  shapeMessageIconFormula?: string | null;
}

async function show(options: ShowDialog) {
  try {
    loading.value = true;
    problems.value = undefined;
    styleId.value = options.id;

    if (options.id !== undefined) {
      title.value = dialogTranslations.edit.title;
    } else {
      title.value = dialogTranslations.create.title;
    }

    color.value = '4CAF50';
    name.value = options.name ?? '';
    shapeLabelFormula.value = options.shapeLabelFormula ?? '';
    shapeColorFormula.value = options.shapeColorFormula ?? '';
    shapeOutlineColorFormula.value = options.shapeOutlineColorFormula ?? '';
    shapeFontSizeFormula.value = options.shapeFontSizeFormula ?? '';
    shapeOutlineSizeFormula.value = options.shapeOutlineSizeFormula ?? '';
    shapeMessageIconFormula.value = options.shapeMessageIconFormula ?? '';
    vehicleColorFormula.value = options.vehicleColorFormula ?? '';
    initialValues.value = options;
    visible.value = true;

  } finally {
    loading.value = false;
  }
}

async function onSave() {

  try {
    loading.value = true;

    const id = styleId.value;

    if (id === undefined) {
      await create();
    } else {
      await update(id);
    }

  } finally {
    loading.value = false;
  }
}

async function create() {
  const request: CreateMapStyleRequest = {
    style: {
      name: name.value,

      shapeColorFormula: shapeColorFormula.value,
      shapeLabelFormula: shapeLabelFormula.value,
      shapeOutlineColorFormula: shapeOutlineColorFormula.value,
      shapeOutLineSizeFormula: shapeOutlineSizeFormula.value,
      shapeFontSizeFormula: shapeFontSizeFormula.value,
      shapeMessageIconFormula: shapeMessageIconFormula.value,

      vehicleColorFormula: vehicleColorFormula.value
    }
  };

  const response = await createMapStyleApi.execute(request);

  if (!response.ok) {
    problems.value = response.details;
    return;
  }

  visible.value = false;
}

async function update(id: string) {

  const request: UpdateMapStyleRequest = {
    styleId: id,
    style: {
      name: name.value,
      shapeColorFormula: shapeColorFormulaChanged.value ? shapeColorFormula.value : undefined,
      shapeLabelFormula: shapeLabelFormulaChanged.value ? shapeLabelFormula.value : undefined,
      shapeOutlineColorFormula: shapeOutlineColorFormulaChanged.value ? shapeOutlineColorFormula.value : undefined,
      shapeOutlineSizeFormula: shapeOutlineSizeFormulaChanged.value ? shapeOutlineSizeFormula.value : undefined,
      vehicleColorFormula: vehicleColorFormulaChanged.value ? vehicleColorFormula.value : undefined,
      shapeFontSizeFormula: shapeFontSizeFormula.value ? shapeFontSizeFormula.value : undefined,
      shapeMessageIconFormula: shapeMessageIconFormula.value ? shapeMessageIconFormula.value : undefined
    }
  };

  const response = await updateMapStyle.execute(request);

  if (!response.ok) {
    problems.value = response.details;
    return;
  }

  styleId.value = undefined;
  visible.value = false;
}

function onHide() {
  styleId.value = undefined;
}

function compilationMessage(compilation?: CompilationResult) {
  if (compilation?.success) return;
  return compilation?.message;
}

</script>

<template>
  <Dialog class="p-dialog-maximized edit-map-style-dialog" modal ref="dialog" v-model:visible="visible" :header="title"
    @hide="onHide" :closeOnEscape="false">
    <Splitter>
        <SplitterPanel class="form-panel">
          <div class="style-form">
            <ProblemDetailsView v-model="problems"></ProblemDetailsView>
            <Message severity="info">{{ dialogTranslations.tip }}</Message>
            <Message severity="info">
              {{ dialogTranslations.documentation }}

              <Button as="a" :label="dialogTranslations.documentationLinkText"
                href="https://wiki.datapolarna.se/wiki/SILog_map_styles" target="_blank" rel="noopener"
                icon="pi pi-external-link" iconPos="right"></Button>
            </Message>

            <div class="field" :class="{ changed: nameChanged }">
              <label for="edit-map-style-dialog-name">{{ translations.generic.name }}</label>
              <InputText autocomplete="off" v-model:modelValue="name" autofocus id="edit-map-style-dialog-name">
              </InputText>
              <Message v-if="nameInvalid" severity="error" variant="simple">{{ nameInvalid }}</Message>
            </div>

            <div class="field">
              <label for="edit-product-dialog-color">{{ dialogTranslations.colorPicker }}</label>
              <InputGroup>
                <InputGroupAddon>
                  <ColorPicker v-model:model-value="color" format="hex"></ColorPicker>
                </InputGroupAddon>
                <InputText autocomplete="off" id="edit-product-dialog-color" v-model:model-value="color">
                </InputText>
              </InputGroup>
            </div>

            <FieldSet :legend="dialogTranslations.shapeSet">

              <div class="field" :class="{ changed: shapeLabelFormulaChanged }">
                <label for="edit-map-style-dialog-shape-label-formula">{{ dialogTranslations.labelFormula }}</label>
                <FormulaEditor :lang="shapeLanguage" v-model:modelValue="shapeLabelFormula"
                  id="edit-map-style-dialog-shape-label-formula"></FormulaEditor>
                <Message v-if="shapeLabelCompilationMessage" severity="error" variant="simple">{{ shapeLabelCompilationMessage }}</Message>
              </div>

              <div class="field" :class="{ changed: shapeColorFormulaChanged }">
                <label for="edit-map-style-dialog-shape-color-formula">{{ dialogTranslations.colorFormula }}</label>
                <FormulaEditor :lang="shapeLanguage" v-model:modelValue="shapeColorFormula"
                  id="edit-map-style-dialog-shape-color-formula"></FormulaEditor>
                <Message v-if="shapeColorCompilationMessage" severity="error" variant="simple">{{ shapeColorCompilationMessage }}</Message>
              </div>

              <div class="field" :class="{ changed: shapeOutlineColorFormulaChanged }">
                <label for="edit-map-style-dialog-shape-outline-color-formula">{{ dialogTranslations.outlineColorFormula
                  }}</label>
                <FormulaEditor :lang="shapeLanguage" v-model:modelValue="shapeOutlineColorFormula"
                  id="edit-map-style-dialog-shape-outline-color-formula"></FormulaEditor>
                <Message v-if="shapeOutlineColorCompilationMessage" severity="error" variant="simple">{{ shapeOutlineColorCompilationMessage }}</Message>
              </div>

              <div class="field" :class="{ changed: shapeOutlineSizeFormulaChanged }">
                <label for="edit-map-style-dialog-shape-outline-size-formula">{{ dialogTranslations.outlineSizeFormula
                  }}</label>
                <FormulaEditor :lang="shapeLanguage" v-model:modelValue="shapeOutlineSizeFormula"
                  id="edit-map-style-dialog-shape-outline-size-formula"></FormulaEditor>
                <Message v-if="shapeOutlineSizeCompilationMessage" severity="error" variant="simple">{{ shapeOutlineSizeCompilationMessage }}</Message>
              </div>

              <div class="field" :class="{ changed: shapeFontSizeFormulaChanged }">
                <label for="edit-map-style-dialog-shape-font-size-formula">{{ dialogTranslations.fontSizeFormula
                  }}</label>
                <FormulaEditor :lang="shapeLanguage" v-model:modelValue="shapeFontSizeFormula"
                  id="edit-map-style-dialog-shape-font-size-formula"></FormulaEditor>
                <Message v-if="shapeFontSizeCompilationMessage" severity="error" variant="simple">{{ shapeFontSizeCompilationMessage }}</Message>
              </div>

              <div class="field" :class="{ changed: shapeMessageIconFormulaChanged }">
                <label for="edit-map-style-dialog-shape-message-icon-formula">{{ dialogTranslations.messageIconFormula
                  }}</label>
                <FormulaEditor :lang="shapeLanguage" v-model:modelValue="shapeMessageIconFormula"
                  id="edit-map-style-dialog-shape-message-icon-formula"></FormulaEditor>
                <Message v-if="shapeMessageIconCompilationMessage" severity="error" variant="simple">{{ shapeMessageIconCompilationMessage }}</Message>
              </div>

            </FieldSet>

            <FieldSet :legend="dialogTranslations.vehicleSet">

              <div class="field" :class="{ changed: vehicleColorFormulaChanged }">
                <label for="edit-map-style-dialog-vehicle-color-formula">{{ dialogTranslations.colorFormula }}</label>
                <FormulaEditor :lang="vehicleLanguage" v-model:modelValue="vehicleColorFormula"
                  id="edit-map-style-dialog-vehicle-color-formula"></FormulaEditor>
                <Message v-if="vehicleColorCompilationMessage" severity="error" variant="simple">{{ vehicleColorCompilationMessage }}</Message>
              </div>

            </FieldSet>

            <div class="actions">
              <Button class="submit-button" :label="translations.generic.save" @click="onSave" :loading="loading"></Button>
            </div>
          </div>
        </SplitterPanel>
        <SplitterPanel>
          <PreviewMapView :map-style="previewMapStyle.mapStyle" class="preview-map"></PreviewMapView>
        </SplitterPanel>
      </Splitter>
  </Dialog>
</template>

<style scoped>
.style-form {
  padding-block: .5rem;
  padding-inline: .5rem;
  display: grid;
  row-gap: .5rem;
  column-gap: .5rem;
  grid-template-columns: 1fr;
  min-width: 30rem;
}

.description-field {
  grid-column: 1/4;
}

.field {
  display: flex;
  flex-direction: column;
}

.submit-button {
  justify-self: end;
}

.log-fields {
  grid-column: 1/4;
}

.log-fields :deep(.p-fieldset-content) {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  row-gap: .5rem;
  column-gap: .5rem;
}

.field :deep(.p-inputnumber-input) {
  text-align: right;
}

.toggle-field {
  display: flex;
  align-items: center;
  column-gap: .5rem;
}

.actions {
  display: flex;
  flex-direction: row;
  justify-content: end;
  gap: .5rem;
}

.preview-map {
  max-height: 100%;
}

.form-panel {
  overflow-y: auto;
}

</style>

<style>
.edit-map-style-dialog .p-dialog-content {
  overflow-x: hidden;
  overflow-y: hidden;
}

.edit-map-style-dialog .p-fieldset {
  display: grid;
}

.edit-map-style-dialog .p-fieldset-content-container {
  overflow-x: hidden;
}

.edit-map-style-dialog .p-splitter {
  min-height: 100%;
  max-height: 100%;

}
</style>
